import React, {useCallback, useEffect, useState} from "react";
import {Box, Paper, Typography} from "@mui/material";
import {Add} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {Col} from "reactstrap";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Button from "../../../../../../components/base/button/button";
import {CustomerAddressTypes} from "../../../../../../../core/constants/enums";
import UpsertVendorAddressDialog from "../../../../../../components/dialogs/upsert-vendor-address";
import VendorAddressCard, {VendorAddressCardActions} from "./address-card";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const initDialog = {open: false, address: undefined, editing: false, typeId: CustomerAddressTypes.shipping};

/**
 * Normalizes an API address row for display + edit form.
 */
const mapVendorAddressFromApi = (raw, {isBilling = false} = {}) => {
    if (!raw)
        return null;
    const provinceCode = raw.provinceCode
        ?? (typeof raw.province === 'string' ? raw.province : raw.province?.code)
        ?? '';
    const countryCode = raw.countryCode
        ?? (typeof raw.country === 'string' ? raw.country : raw.country?.code)
        ?? '';
    const provinceId = raw.provinceId
        ?? (typeof raw.province === 'object' ? raw.province?.id : undefined);
    const countryId = raw.countryId
        ?? (typeof raw.country === 'object' ? raw.country?.id : undefined);
    return {
        ...raw,
        line1: raw.line1 ?? raw.streetAddress ?? '',
        line2: raw.line2 ?? raw.aptNo ?? '',
        line3: raw.line3 ?? '',
        line4: raw.line4 ?? '',
        city: raw.city ?? '',
        postalCode: raw.postalCode ?? '',
        provinceCode,
        countryCode,
        provinceId,
        countryId,
        _isBilling: isBilling,
        isDefault: raw.isDefault ?? false,
        type: {
            id: isBilling ? CustomerAddressTypes.billing : CustomerAddressTypes.shipping,
        },
    };
};

/**
 * Vendor addresses with addresses-v2 presentation and CleanAF vendor APIs.
 */
const VendorAddressesSection = ({vendorId, loading: loadingVendor, deleting = false}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.addresses'});
    const params = useParams();
    const resolvedVendorId = Number(vendorId ?? params.vendorId);
    const [loading, setLoading] = useState(true);
    const [shippingAddresses, setShippingAddresses] = useState([]);
    const [billingAddress, setBillingAddress] = useState(null);
    const [settingAsDefault, setSettingAsDefault] = useState({from: null, to: null});
    const [dialog, setDialog] = useState(initDialog);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canManageAccountInfo = operatorAccessLevels.packages.base.manageVendorAccountInfo;
    const allowCreation = canManageAccountInfo;
    const allowRemoval = canManageAccountInfo;
    const allowSetDefault = canManageAccountInfo;

    /**
     * Loads vendor shipping and billing addresses.
     */
    const loadAddresses = useCallback(async (showLoading = true) => {
        if (!resolvedVendorId) {
            setLoading(false);
            return;
        }
        if (showLoading)
            setLoading(true);
        const response = await Api.getVendorAddresses(resolvedVendorId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const payload = response.data ?? {};
            const shippingRaw = payload.shippingAddresses ?? payload.shipping ?? [];
            let billingRaw = null;
            if (payload.billingAddress) {
                billingRaw = Array.isArray(payload.billingAddress)
                    ? payload.billingAddress[0]
                    : payload.billingAddress;
            } else if (payload.billingAddresses?.[0]) {
                billingRaw = payload.billingAddresses[0];
            } else if (payload.billing && !Array.isArray(payload.billing)) {
                billingRaw = payload.billing;
            } else if (Array.isArray(payload.billing) && payload.billing[0]) {
                billingRaw = payload.billing[0];
            }
            setShippingAddresses(
                (shippingRaw ?? []).map((a) => mapVendorAddressFromApi(a)).filter(Boolean),
            );
            setBillingAddress(billingRaw ? mapVendorAddressFromApi(billingRaw, {isBilling: true}) : null);
        } else {
            setShippingAddresses([]);
            setBillingAddress(null);
        }
        if (showLoading)
            setLoading(false);
    }, [isMounted, resolvedVendorId]);

    useEffect(() => {
        void loadAddresses();
    }, [loadAddresses]);

    /**
     * Sets a shipping address as default ({ id } body).
     */
    const setAsDefault = useCallback(async (address) => {
        if (!canManageAccountInfo) return false;
        const currentDefaultId = shippingAddresses.find((a) => a.isDefault)?.id ?? null;
        setSettingAsDefault({from: currentDefaultId, to: address.id});
        const response = await Api.setVendorDefaultShipping({id: address.id});
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            await loadAddresses(false);
        }
        if (!isMounted())
            return false;
        setSettingAsDefault({from: null, to: null});
        return response?.resultFlag ?? false;
    }, [canManageAccountInfo, isMounted, loadAddresses, shippingAddresses]);

    /**
     * Deletes a vendor address.
     */
    const removeAddress = useCallback(async (address) => {
        if (!canManageAccountInfo) return false;
        const response = await Api.deleteVendorAddress(address.id);
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            void loadAddresses(false);
        }
        return response?.resultFlag ?? false;
    }, [canManageAccountInfo, isMounted, loadAddresses]);

    /**
     * Upserts address; add shipping/billing include provinceId from form province object.
     */
    const upsertAddress = useCallback(async (values) => {
        if (!canManageAccountInfo) return false;
        const countryCode = values.country?.code
            ?? (typeof values.country === 'string' ? values.country : '')
            ?? '';
        const provinceCode = values.province?.code
            ?? (typeof values.province === 'string' ? values.province : '')
            ?? '';
        const provinceId = values.province?.id;
        const base = {
            line1: values.line1 ?? '',
            line2: values.line2 ?? '',
            line3: values.line3 ?? '',
            line4: values.line4 ?? '',
            city: values.city ?? '',
            postalCode: values.postalCode ?? '',
            province: provinceCode,
            country: countryCode,
        };
        let response;
        if (dialog.editing) {
            response = await Api.updateVendorAddress({
                id: dialog.address.id,
                ...base,
                ...(provinceId != null ? {provinceId} : {}),
            });
        } else if (Number(values.typeId) === CustomerAddressTypes.billing) {
            response = await Api.addVendorBillingAddress({
                vendorId: resolvedVendorId,
                ...base,
                provinceId,
            });
        } else {
            response = await Api.addVendorShippingAddress({
                vendorId: resolvedVendorId,
                ...base,
                provinceId,
            });
        }
        if (response?.resultFlag) {
            void loadAddresses(false);
        }
        return response?.resultFlag ?? false;
    }, [canManageAccountInfo, dialog.address?.id, dialog.editing, loadAddresses, resolvedVendorId]);

    /**
     * Opens the upsert dialog for shipping or billing.
     */
    const openDialog = useCallback((typeId, address = undefined) => {
        setDialog({
            open: true,
            address: address
                ? {...address, type: {id: typeId}}
                : undefined,
            editing: !!address,
            typeId,
        });
    }, []);

    const busy = loadingVendor || loading || deleting;

    const withTypeLabel = (address, typeId) => ({
        ...address,
        typeLabel: typeId === CustomerAddressTypes.billing ? t('billing') : t('shipping'),
    });

    return (
        <Paper sx={{p: 2, height: '100%'}}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mb: 3,
            }}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    {t('billingTitle')}
                </Typography>
                {
                    allowCreation && !billingAddress &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={busy}
                        onClick={() => openDialog(CustomerAddressTypes.billing)}
                    >
                        <Add className={'icon small'} style={{marginRight: 4}}/>
                        {t('add')}
                    </Button>
                }
            </Box>
            <div className={'address-cards-container'}>
                {
                    (loadingVendor || loading) &&
                    Array(1).fill(null).map((_, i) => (
                        <Col key={i} xs={12} md={6} xl={4} className={'loading extraLarge mb-4'}>
                            <div/>
                        </Col>
                    ))
                }
                {
                    !loadingVendor && !loading && billingAddress &&
                    <VendorAddressCard
                        address={withTypeLabel(billingAddress, CustomerAddressTypes.billing)}
                        edit={() => openDialog(CustomerAddressTypes.billing, billingAddress)}
                        remove={() => removeAddress(billingAddress)}
                        allowSetDefault={false}
                        allowRemoval={allowRemoval}
                        disabled={deleting}
                        readonly={!canManageAccountInfo}
                        excludeActions={[VendorAddressCardActions.setDefault]}
                    />
                }
                {
                    !loadingVendor && !loading && !billingAddress &&
                    <div className={'d-flex justify-content-center w-100 my-4 py-3'}>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('emptyBilling')}
                        </Typography>
                    </div>
                }
            </div>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mb: 3,
                mt: 4,
            }}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    {t('shippingTitle')}
                </Typography>
                {
                    allowCreation &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={busy}
                        onClick={() => openDialog(CustomerAddressTypes.shipping)}
                    >
                        <Add className={'icon small'} style={{marginRight: 4}}/>
                        {t('add')}
                    </Button>
                }
            </Box>
            <div className={'address-cards-container'}>
                {
                    (loadingVendor || loading) &&
                    Array(2).fill(null).map((_, i) => (
                        <Col key={i} xs={12} md={6} xl={4} className={'loading extraLarge mb-4'}>
                            <div/>
                        </Col>
                    ))
                }
                {
                    !loadingVendor && !loading && shippingAddresses.map((address) => (
                        <VendorAddressCard
                            key={address.id}
                            address={withTypeLabel(address, CustomerAddressTypes.shipping)}
                            edit={() => openDialog(CustomerAddressTypes.shipping, address)}
                            setDefault={() => setAsDefault(address)}
                            remove={() => removeAddress(address)}
                            allowSetDefault={allowSetDefault}
                            allowRemoval={allowRemoval && shippingAddresses.length > 1}
                            settingAsDefault={settingAsDefault.to === address.id || settingAsDefault.from === address.id}
                            disabled={deleting}
                            readonly={!canManageAccountInfo}
                            excludeActions={
                                address.isDefault
                                    ? [VendorAddressCardActions.setDefault, VendorAddressCardActions.remove]
                                    : []
                            }
                        />
                    ))
                }
                {
                    !loadingVendor && !loading && !shippingAddresses.length &&
                    <div className={'d-flex justify-content-center w-100 my-4 py-3'}>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('emptyShipping')}
                        </Typography>
                    </div>
                }
            </div>
            {
                dialog.open &&
                <UpsertVendorAddressDialog
                    open={dialog.open}
                    close={() => setDialog(initDialog)}
                    address={dialog.address ?? {
                        type: {id: dialog.typeId},
                        _isBilling: dialog.typeId === CustomerAddressTypes.billing,
                    }}
                    editing={dialog.editing}
                    submit={upsertAddress}
                />
            }
        </Paper>
    );
};

export default VendorAddressesSection;
